/**
 * Notification and Web Audio Sound Engine for MagicMail Webmail
 */

let audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!audioCtx) {
    const AudioContextClass =
      window.AudioContext || (window as any).webkitAudioContext;
    if (AudioContextClass) {
      audioCtx = new AudioContextClass();
    }
  }
  if (audioCtx && audioCtx.state === "suspended") {
    audioCtx.resume().catch(() => {});
  }
  return audioCtx;
}

/**
 * Synthesize a clean, pleasant, high-fidelity 2-tone notification chime
 * using the Web Audio API with zero asset requests.
 */
export function playNotificationSound() {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    const now = ctx.currentTime;

    // Tone 1: E6 (1318.51 Hz) -> Tone 2: A6 (1760.00 Hz)
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gainNode = ctx.createGain();

    osc1.type = "sine";
    osc2.type = "sine";

    osc1.frequency.setValueAtTime(880, now); // A5
    osc1.frequency.exponentialRampToValueAtTime(1318.5, now + 0.08); // E6

    osc2.frequency.setValueAtTime(1318.5, now + 0.08);
    osc2.frequency.exponentialRampToValueAtTime(1760, now + 0.16); // A6

    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.2, now + 0.02);
    gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.45);

    osc1.connect(gainNode);
    osc2.connect(gainNode);
    gainNode.connect(ctx.destination);

    osc1.start(now);
    osc1.stop(now + 0.15);

    osc2.start(now + 0.08);
    osc2.stop(now + 0.45);
  } catch (err) {
    console.warn("Could not play notification sound:", err);
  }
}

/**
 * Request desktop/mobile notification permission
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "denied";
  }

  if (Notification.permission === "granted") {
    return "granted";
  }

  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch (err) {
    console.error("Error requesting notification permission:", err);
    return "denied";
  }
}

/**
 * Trigger a native desktop or mobile push notification
 */
export async function showEmailNotification(options: {
  title: string;
  body: string;
  folderId?: string;
  emailId?: string;
  onClick?: () => void;
}) {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return;
  }

  if (Notification.permission !== "granted") {
    return;
  }

  const notificationTitle = options.title || "Nuevo correo en MagicMail";
  const notificationOptions: NotificationOptions = {
    body: options.body || "Has recibido un nuevo mensaje.",
    icon: "/icon-192.png",
    badge: "/icon-192.png",
    tag: options.emailId ? `mail-${options.emailId}` : "magicmail-notification",
    data: {
      url: "/",
      folderId: options.folderId || "INBOX",
      emailId: options.emailId,
    },
  };

  try {
    // Prefer Service Worker registration if available
    if ("serviceWorker" in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration && "showNotification" in registration) {
        await registration.showNotification(notificationTitle, notificationOptions);
        return;
      }
    }

    // Fallback to standard window Notification
    const notif = new Notification(notificationTitle, notificationOptions);
    notif.onclick = (e) => {
      e.preventDefault();
      window.focus();
      if (options.onClick) {
        options.onClick();
      }
      notif.close();
    };
  } catch (err) {
    console.error("Error showing notification:", err);
  }
}

/**
 * Updates the app icon badge in installed PWA, Android & desktop environments
 * using the W3C Badging API (navigator.setAppBadge).
 */
export async function updateAppBadge(count: number) {
  if (typeof navigator === "undefined") return;

  try {
    if ("setAppBadge" in navigator) {
      if (count > 0) {
        await (navigator as any).setAppBadge(count);
      } else {
        await (navigator as any).clearAppBadge();
      }
    }
  } catch (err) {
    // Unsupported or permission restricted
  }
}

/**
 * Clears the app icon badge
 */
export async function clearAppBadge() {
  if (typeof navigator === "undefined") return;

  try {
    if ("clearAppBadge" in navigator) {
      await (navigator as any).clearAppBadge();
    }
  } catch (err) {
    // Unsupported
  }
}
